import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';  // Import Axios
import './SpendSection.css';

const SpendSection = ({ setNotification, onNavigateToCollection, refreshTokenIfNeeded, loggedInUser, }) => {
    const [balance, setBalance] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isPurchaseComplete, setIsPurchaseComplete] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [itemData, setItemData] = useState({});
    const [guestView, setGuestView] = useState(false);

    const fetchBalance = async () => {
      try {
          const response = await axios.get(`/api/user-balance/`);
          setBalance(response.data.amount);  // Set the balance dynamically
          setGuestView(false);
      } catch (error) {
          console.error('Error fetching balance:', error);
          if (error.response && error.response.status === 401) {
            try {
              console.log('Refreshing token...');
              await refreshTokenIfNeeded();
      
              // Retry GET request
              console.log('Retrying get balance request after token refresh...');
              const response = await axios.get(`/api/user-balance/`);
              setBalance(response.data.amount);  // Set the balance dynamically
              setGuestView(false);
            } catch (retryError) {
              console.error('Retry failed after token refresh:', retryError);
              setGuestView(true);
            }
          }
          else {
            setGuestView(true);
          }
      }
    };

    useEffect(() => {
      if (loggedInUser && loggedInUser !== '') {
        fetchBalance();
      }
      else if (loggedInUser === '')
      {
        setGuestView(true);
        setBalance(0);
      }
      else
      {
        // Do nothing
      }
    }, [loggedInUser]); 

    const items = [
      { 
        id: 1, 
        name: "Devil Fruit Lotto", 
        price: 100,
        description: "A chance to get a random Devil Fruit.",
        image: "https://images.gomugomuguess.com/one-piece-spend-section-images/Devil_Fruit_Lotto.webp",
        category: "paramecia,zoan,logia",
        link: "https://onepiece.fandom.com/wiki/Devil_Fruit",
      },
      { 
        id: 2, 
        name: "Sword Lotto", 
        price: 100,
        description: "A chance to get a random legendary blade.",
        image: "https://images.gomugomuguess.com/one-piece-spend-section-images/Sword_Lotto.webp",
        category: "sword",
        link: "https://onepiece.fandom.com/wiki/Meito",
      },
      { 
        id: 3, 
        name: "SMILE Lotto", 
        price: 50,
        description: "A chance to get a random SMILE Fruit.",
        image: "https://images.gomugomuguess.com/one-piece-spend-section-images/Devil_Fruit_SMILE_Lotto.webp",
        category: "smile",
        link: "https://onepiece.fandom.com/wiki/SMILE",
      },
      { 
        id: 4, 
        name: "Paramecia Lotto", 
        price: 150,
        description: "A high chance to get a Paramecia Devil Fruit.",
        image: "https://images.gomugomuguess.com/one-piece-spend-section-images/Devil_Fruit_Paramecia.webp",
        category: "paramecia",
        link: "https://onepiece.fandom.com/wiki/Paramecia",
      },
      { 
        id: 5, 
        name: "Zoan Lotto", 
        price: 200,
        description: "A high chance to get a Zoan Devil Fruit.",
        image: "https://images.gomugomuguess.com/one-piece-spend-section-images/Devil_Fruit_Zoan.webp",
        category: "zoan",
        link: "https://onepiece.fandom.com/wiki/Zoan",
      },
      { 
        id: 6, 
        name: "Logia Lotto", 
        price: 250,
        description: "A high chance to get a Logia Devil Fruit.",
        image: "https://images.gomugomuguess.com/one-piece-spend-section-images/Devil_Fruit_Logia.webp",
        category: "logia",
        link: "https://onepiece.fandom.com/wiki/Logia",
      },
    ];

    // Base URLs for each category
    const categoryBaseUrls = {
      smile: "https://images.gomugomuguess.com/one-piece-collection-section-images/one-piece-smile-images/",
      paramecia: "https://images.gomugomuguess.com/one-piece-collection-section-images/one-piece-paramecia-images/",
      zoan: "https://images.gomugomuguess.com/one-piece-collection-section-images/one-piece-zoan-images/",
      logia: "https://images.gomugomuguess.com/one-piece-collection-section-images/one-piece-logia-images/",
      sword: "https://images.gomugomuguess.com/one-piece-collection-section-images/one-piece-sword-images/",
      food: "https://images.gomugomuguess.com/one-piece-collection-section-images/one-piece-food-images/",
    };

    const handlePurchase = () => {
      if (selectedItem && loggedInUser && loggedInUser !== '') {
        // Fetch the random item data for the selected category
        fetchItemData(selectedItem.category);
        setIsAnimating(true); // Start the animation
        setTimeout(() => {
          setBalance((prevBalance) => prevBalance - selectedItem.price);
          setIsPurchaseComplete(true);
          setIsAnimating(false); // End the animation
        }, 3600); // Animation duration
      }
    };
  
    useEffect(() => {
      if (itemData.purchaseCategory === "Unknown Category") {
        const timer = setTimeout(() => {
          handleCloseDialog();
        }, 0); // Wait for 1.8 seconds before closing dialog

        // Cleanup the timeout if the component unmounts or dependencies change
        return () => clearTimeout(timer);
      }
    }, [itemData]);

    const closeDialog = () => {
      setSelectedItem(null);
      setIsPurchaseComplete(false);
    };

    const handleCloseDialog = () => {
      if (isPurchaseComplete && itemData.purchaseCategory !== "Unknown Category" && itemData.purchaseCategory !== "food") {
        setNotification(true); 
      }
      closeDialog(); 
    };

    const handleCollectionRedirect = () => {
      if (isPurchaseComplete) {
        setNotification(false); 
        onNavigateToCollection();
      }
    };

    const fetchItemFromApi = async (category) => {
      try {
        // Make an API call to get random item data for the selected category
        const response = await axios.post(`/api/random-item/${category}/`);
        const data = response.data.item;
    
        // Use the category from the API response to get the correct base URL
        const categoryFromApi = data.category;  // The category returned from the API response
        const categoryBaseUrl = categoryBaseUrls[categoryFromApi];  // Get the base URL dynamically
    
        // Form the full purchase image URL
        const purchaseImageUrl = `${categoryBaseUrl}${data.image_url}`;
    
        return {
          purchaseImage: purchaseImageUrl,
          purchaseName: data.name,
          purchaseCategory: data.category,
          purchaseProfileSuffix: data.profile_suffix,
        };
    
      } catch (error) {
        console.error("Error fetching item data:", error);
        if (error.response && error.response.status === 401) {
          return null;  // Return null to indicate the need for a token refresh
        }
        return {
          purchaseImage: "", // Fallback image
          purchaseName: "Unknown Item",
          purchaseCategory: "Unknown Category",
          purchaseProfileSuffix: "Unknown Profile Suffix",
        };
      }
    };
    
    const fetchItemData = async (category) => {
      try {
        let itemData = await fetchItemFromApi(category);  // First attempt to fetch item data
    
        if (itemData === null) {
          console.log('Refreshing token...');
          await refreshTokenIfNeeded();
    
          // Retry the API call after token refresh
          console.log('Retrying random item request after token refresh...');
          itemData = await fetchItemFromApi(category);  // Retry fetching data
    
          if (!itemData) {
            // Fallback in case retry fails
            itemData = {
              purchaseImage: "", // Fallback image
              purchaseName: "Unknown Item",
              purchaseCategory: "Unknown Category",
              purchaseProfileSuffix: "Unknown Profile Suffix",
            };
          }
        }
    
        setItemData(itemData);
        //The following never runs given the setup, but it's ok
      } catch (error) {
        console.error('Unexpected error:', error);
        setItemData({
          purchaseImage: "", // Fallback image
          purchaseName: "Unknown Item",
          purchaseCategory: "Unknown Category",
          purchaseProfileSuffix: "Unknown Profile Suffix",
        });
      }
    };

    const handleItemSelect = (item) => {
      setSelectedItem(item);
      setIsPurchaseComplete(false);
    };

    return (
      <div className="spend-section">
        <h1><a href="https://onepiece.fandom.com/wiki/Underworld" target="_blank" rel="noopener noreferrer">Underworld Market</a></h1>
        {guestView ? (
        <h3>"You're not from here" – <a href="https://onepiece.fandom.com/wiki/Cross_Guild" target="_blank" rel="noopener noreferrer">Cross Guild Lackey</a></h3>
        ) : (
        <h3>"Try Your Luck!" – <a href="https://onepiece.fandom.com/wiki/Lu_Feld" target="_blank" rel="noopener noreferrer">"God of Fortune" Lu Feld</a></h3>  
        )}
        <div className="balance-display">
          {guestView ? (
            <span className="guest-message">Berries Stolen</span>
          ) : (
            <div className="balance-row">
              <span className="balance-label">Berry Wallet</span>
              <span className="balance">
                  <a href="https://onepiece.fandom.com/wiki/Belly" target="_blank" rel="noopener noreferrer">
                      B${balance.toLocaleString()}
                  </a>
              </span>
            </div>
          )}
        </div>
        <div className={`shop-grid `}>
          {guestView && (
            <div className="shop-overlay">
              <div className="market-locked">Market Locked</div>
              <div className="log-in">Log In to Access</div>
            </div>
          )}
          {items.map(item => (
            <div key={item.id} className="item-card">
              <img src={item.image} alt={item.name} className="item-image" />
              <h3 className="item-name">
                <a href={item.link} target="_blank" rel="noopener noreferrer">{item.name}</a>
              </h3>
              <p className="item-description">{item.description}</p>
              <p className="item-price">B${item.price.toLocaleString()}</p>
              <button 
                className="buy-button"
                onClick={() => handleItemSelect(item)}
                disabled={balance < item.price}
              >
                {balance < item.price ? 'Buy' : 'Buy'}
              </button>
            </div>
          ))}
        </div>
  
        {selectedItem && (
          <div className="dialog-overlay">
            <div className="dialog-content">
              <div className="dialog-header">
                <h2 className="dialog-title">
                  {isPurchaseComplete ? 'Purchase Complete' : 'Buy Item'}
                </h2>
                {!isAnimating && (
                  <button
                    className="close-button"
                    onClick={handleCloseDialog}
                  >
                    <X size={35} />
                  </button>
                )}
              </div>
              <div className="dialog-divider" />
              <p className="dialog-confirmation">
                {isPurchaseComplete
                  ? (
                    <>
                      You have received "{itemData.purchaseName}" for <strong>B${selectedItem.price.toLocaleString()}</strong>.
                    </>
                  )
                  : (
                    <>
                      Would you like to buy "{selectedItem.name}" for <strong>B${selectedItem.price.toLocaleString()}</strong>?
                    </>
                  )
                }
              </p>
              <div className="dialog-image-container">
                {isPurchaseComplete ? (
                  <a
                    href={`https://onepiece.fandom.com/wiki/${itemData.purchaseProfileSuffix}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={itemData.purchaseImage}
                      alt={itemData.purchaseName}
                      className="dialog-image"
                    />
                  </a>
                ) : (
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.name}
                    className="dialog-image"
                  />
                )}
              </div>
              <div className="dialog-buttons">
                {isAnimating ? (
                  <div className="loading-animation">
                    <div className="loading-square"></div>
                    <div className="loading-square"></div>
                    <div className="loading-square"></div>
                  </div>
                ) : (
                  <>
                    <button
                      className="buy-now-button"
                      onClick={isPurchaseComplete 
                        ? (itemData.purchaseCategory === "food" ? handleCloseDialog : handleCollectionRedirect)
                        : handlePurchase}
                    >
                      {isPurchaseComplete 
                        ? (itemData.purchaseCategory === "food" ? "Eat it" : "Collection")
                        : "Buy Now"}              
                    </button>
                    <button
                      className="cancel-button"
                      onClick={handleCloseDialog}
                    >
                      {isPurchaseComplete 
                        ? (itemData.purchaseCategory === "food" ? "Toss it" : "Not Now")
                        : "Cancel"}
                    </button>
                  </>
                )}
              </div>
              <p className="balance-after">
                {isPurchaseComplete
                  ? (
                    <>
                      Your balance after this transaction will be <strong>B${balance.toLocaleString()}</strong>.
                    </>
                  )
                  : (
                    <>
                      Your balance after this transaction will be <strong>B${(balance - selectedItem.price).toLocaleString()}</strong>.
                    </>
                  )
                }
              </p>
            </div>
          </div>
        )}
      </div>
    );
};

export default SpendSection;
